import { EliteProspectsPanel } from '@/components/EliteProspectsPanel';
import { ProspectTouchPanel } from '@/components/ProspectTouchPanel';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock,
  CalendarDays,
  BarChart3,
  ChevronLeft,
  CircleDollarSign,
  ClipboardCheck,
  Pencil,
  FileText,
  Link as LinkIcon,
  MapPin,
  MessageSquarePlus,
  Plus,
  Route,
  Trash2,
  Video,
} from 'lucide-react';
import {
  usePlayer,
  type InjuryRecord,
  type NhlContract,
  type PlayerEvent,
  type PlayerSourceRecord,
  type RecentLine,
  type SeasonStatSummary,
} from '@/hooks/usePlayers';
import type { InjuryWithPlayer } from '@/hooks/useInjuries';
import { apiDelete, apiPatch, apiPost } from '@/lib/api';
import { EditInPlace } from '@/components/EditInPlace';
import { InjuryCard } from '@/components/InjuryCard';
import { AddInjuryModal } from '@/components/AddInjuryModal';
import { PlayerAvatar } from '@/components/PlayerAvatar';
import { normalizeHttpUrl, parsePlayerVideoMetadata } from '../../shared/player-media';
import { parseProspectTouchTimelineMetadata } from '../../shared/prospect-engagement';
import {
  ageFromDob,
  classificationLabel,
  formatDob,
  formatHeight,
  formatWeight,
  priorityLabel,
} from '@/lib/formatters';

const SUB_TABS = [
  { to: '', label: 'Overview' },
  { to: 'stats', label: 'Stats' },
  { to: 'timeline', label: 'Timeline' },
  { to: 'endorsements', label: 'Endorsements' },
  { to: 'viewings', label: 'Viewings' },
  { to: 'branding', label: 'Branding' },
  { to: 'video', label: 'Video' },
  { to: 'schedule', label: 'Schedule' },
  { to: 'injuries', label: 'Injuries' },
] as const;

type PlayerSubTab = (typeof SUB_TABS)[number]['to'];

export function PlayerFile() {
  const { playerId } = useParams();
  const [searchParams] = useSearchParams();
  const subTab = normalizeSubTab(searchParams.get('tab'));

  const { data, isLoading, isError } = usePlayer(playerId);
  const qc = useQueryClient();

  if (!playerId) return <Navigate to="/people" replace />;

  if (isLoading) {
    return (
      <section className="space-y-5">
        <BackBar />
        <div className="h-14 animate-pulse rounded-control bg-border/30" />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="h-72 animate-pulse rounded-control bg-border/30" />
          <div className="h-72 animate-pulse rounded-control bg-border/30" />
        </div>
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section className="space-y-5">
        <BackBar />
        <div className="rounded-control border border-flag-critical/30 bg-flag-critical/5 p-4 text-sm text-flag-critical">
          Couldn&apos;t load this player.
        </div>
      </section>
    );
  }

  const p = data.player;
  const name = p.displayName ?? `${p.firstName} ${p.lastName}`;
  const age = ageFromDob(p.dob ?? null);
  const prospectAge = age ?? (p.birthYear ? new Date().getFullYear() - p.birthYear : null);
  const activeInjuries = data.injuries.filter((i) => i.status === 'active');

  async function patch(field: string, value: string | number | null) {
    await apiPatch(`/api/players/${playerId}`, { [field]: value });
    qc.invalidateQueries({ queryKey: ['player', playerId] });
    qc.invalidateQueries({ queryKey: ['players'] });
  }

  return (
    <section className="space-y-5">
      <BackBar />

      <header className="overflow-hidden rounded-control border border-border bg-white">
        <div className="flex gap-4 p-4 sm:p-5">
          <PlayerAvatar
            firstName={p.firstName}
            lastName={p.lastName}
            displayName={name}
            nhlId={p.nhlId}
            imageUrl={
              p.headshotUrl ??
              p.heroImageUrl ??
              (p.mediaCheckedAt ? null : data.externalProfile?.headshot)
            }
            mediaChecked={Boolean(p.mediaCheckedAt)}
            className="h-24 w-24 rounded-control text-xl sm:h-32 sm:w-32"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <h1 className="text-[28px] font-semibold leading-tight tracking-tight">{name}</h1>
                  {p.jerseyNumber != null && (
                    <span className="font-mono text-base text-muted">#{p.jerseyNumber}</span>
                  )}
                  {p.position && (
                    <span className="font-mono text-base text-muted">· {p.position}</span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 text-[13px] text-muted">
                  <span>{p.currentTeam ?? '—'}</span>
                  <span>·</span>
                  <span>{p.currentLeague ?? '—'}</span>
                  {(p.dob || p.birthYear) && (
                    <>
                      <span>·</span>
                      <span>
                        {p.dob ? `DOB: ${formatDob(p.dob)}` : `Birth year: ${p.birthYear}`}
                        {prospectAge != null && ` (${prospectAge})`}
                      </span>
                    </>
                  )}
                  {p.shoots && (
                    <>
                      <span>·</span>
                      <span>Shoots {p.shoots}</span>
                    </>
                  )}
                  {(p.heightIn || p.weightLb) && (
                    <>
                      <span>·</span>
                      <span>
                        {formatHeight(p.heightIn)}
                        {p.weightLb ? ` ${formatWeight(p.weightLb)}` : ''}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={[
                    'inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[11px]',
                    p.status === 'active'
                      ? 'border-flag-ok/40 bg-flag-ok/5 text-flag-ok'
                      : 'border-border bg-border/30 text-muted',
                  ].join(' ')}
                >
                  {p.status}
                </span>
                <span className="font-mono text-[11px] uppercase tracking-wide text-muted">
                  {classificationLabel(p.classification)}
                  {priorityLabel(p.prospectPriority) && ` · ${priorityLabel(p.prospectPriority)}`}
                </span>
              </div>
            </div>
            {data.externalProfile?.profileUrl && (
              <a
                href={data.externalProfile.profileUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex rounded-full border border-border px-2 py-0.5 font-mono text-[11px] text-muted hover:border-bartlett/40 hover:text-ink"
              >
                NHL profile
              </a>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to={`/people/${playerId}?tab=stats`}
                className={[
                  'focus-ring inline-flex h-9 items-center gap-2 rounded-control border px-3 text-sm font-semibold transition-colors',
                  subTab === 'stats'
                    ? 'border-bartlett bg-bartlett text-white'
                    : 'border-bartlettGold/50 bg-bartlettGold/10 text-bartlett hover:border-bartlett',
                ].join(' ')}
              >
                <BarChart3 size={15} />
                <span>Stats</span>
              </Link>
              <Link
                to={`/people/${playerId}?tab=endorsements`}
                className={[
                  'focus-ring inline-flex h-9 items-center gap-2 rounded-control border px-3 text-sm font-semibold transition-colors',
                  subTab === 'endorsements'
                    ? 'border-bartlett bg-bartlett text-white'
                    : 'border-bartlettGold/50 bg-bartlettGold/10 text-bartlett hover:border-bartlett',
                ].join(' ')}
              >
                <CircleDollarSign size={15} />
                <span>Endorsements</span>
              </Link>
              <Link
                to={`/people/${playerId}?tab=schedule`}
                className={[
                  'focus-ring inline-flex h-9 items-center gap-2 rounded-control border px-3 text-sm font-semibold transition-colors',
                  subTab === 'schedule'
                    ? 'border-bartlett bg-bartlett text-white'
                    : 'border-bartlettGold/50 bg-bartlettGold/10 text-bartlett hover:border-bartlett',
                ].join(' ')}
              >
                <CalendarDays size={15} />
                <span>Schedule</span>
              </Link>
            </div>
          </div>
        </div>
        {activeInjuries.length > 0 && (
          <div className="mx-4 mb-3 inline-flex items-center gap-2 rounded-full border border-flag-warn/30 bg-flag-warn/5 px-3 py-1 font-mono text-[12px] text-flag-warn sm:mx-5">
            <span>⚠ {activeInjuries.length} active injury</span>
          </div>
        )}

        <nav
          aria-label="Sub-tabs"
          className="flex flex-wrap gap-1 border-t border-border px-4 py-3 sm:px-5"
        >
          {SUB_TABS.map((t) => {
            const active = (t.to === '' && subTab === 'overview') || t.to === subTab;
            const url = t.to ? `?tab=${t.to}` : '';
            return (
              <NavLink
                key={t.label}
                to={`/people/${playerId}${url}`}
                end
                className={[
                  'inline-flex h-9 items-center rounded-control border px-3 text-sm font-semibold transition-colors',
                  active
                    ? 'border-bartlett bg-bartlett text-white'
                    : 'border-transparent text-muted hover:border-border hover:bg-bg hover:text-ink',
                ].join(' ')}
              >
                {t.label}
                {t.to === 'injuries' && data.injuries.length > 0 && (
                  <span
                    className={[
                      'ml-1.5 font-mono text-[11px]',
                      active ? 'text-white/80' : 'text-muted',
                    ].join(' ')}
                  >
                    {data.injuries.length}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </header>

      {subTab === 'overview' && (
        <Overview
          p={p}
          externalProfile={data.externalProfile}
          nhlContract={data.nhlContract ?? null}
          recentLines={data.recentLines}
          events={data.events}
          sourceRecords={data.sourceRecords}
          onPatch={patch}
        />
      )}

      {subTab === 'stats' && <StatsSubTab recentLines={data.recentLines} />}

      {subTab === 'timeline' && <TimelineSubTab playerId={playerId} events={data.events} />}

      {subTab === 'endorsements' && <EndorsementsSubTab p={p} onPatch={patch} />}

      {subTab === 'viewings' && <ViewingsSubTab playerId={playerId} events={data.events} />}

      {subTab === 'branding' && <BrandingSubTab p={p} onPatch={patch} />}

      {subTab === 'video' && <VideoSubTab playerId={playerId} events={data.events} />}

      {subTab === 'schedule' && <ScheduleSubTab playerId={playerId} events={data.events} />}

      {subTab === 'injuries' && <InjuriesSubTab playerId={playerId} injuries={data.injuries} />}
    </section>
  );
}

function normalizeSubTab(value: string | null): 'overview' | Exclude<PlayerSubTab, ''> {
  if (value && SUB_TABS.some((tab) => tab.to === value)) return value as Exclude<PlayerSubTab, ''>;
  return 'overview';
}

function InjuriesSubTab({ playerId, injuries }: { playerId: string; injuries: InjuryRecord[] }) {
  const [adding, setAdding] = useState(false);
  // Player-scoped cards have no per-row player chip — InjuryCard expects an
  // InjuryWithPlayer shape, so we map and set player=null.
  const shape = (it: InjuryRecord): InjuryWithPlayer => ({
    id: it.id,
    playerId,
    injuryType: it.injuryType,
    severity: it.severity,
    injuryDate: it.injuryDate,
    gameContext: it.gameContext,
    doctor: it.doctor,
    projectedReturnDate: it.projectedReturnDate,
    returnToPlayDate: it.returnToPlayDate,
    status: it.status,
    firstGameBackFlagged: it.firstGameBackFlagged,
    countdownAlertSent: it.countdownAlertSent,
    notes: it.notes,
    createdAt: 0,
    updatedAt: 0,
    createdBy: it.createdBy,
    updatedBy: it.updatedBy,
    player: null,
  });

  const active = injuries.filter((i) => i.status === 'active');
  const archived = injuries.filter((i) => i.status === 'archived');

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-base font-semibold">
          Active <span className="font-mono text-[12px] text-muted">{active.length}</span>
        </h2>
        <button
          onClick={() => setAdding(true)}
          className="inline-flex h-9 items-center gap-2 rounded-control border border-bartlett bg-bartlett px-3 text-sm font-semibold text-white hover:bg-bartlett/95"
        >
          <Plus size={15} />
          <span>Add Injury</span>
        </button>
      </div>

      {active.length === 0 ? (
        <p className="rounded-control border border-dashed border-border px-3 py-2 text-[13px] italic text-muted">
          No active injuries.
        </p>
      ) : (
        <div className="overflow-hidden rounded-control border border-border bg-white">
          {active.map((injury) => (
            <InjuryCard key={injury.id} injury={shape(injury)} showPlayer={false} />
          ))}
        </div>
      )}

      {archived.length > 0 && (
        <>
          <h2 className="pt-2 text-base font-semibold">
            Archived <span className="font-mono text-[12px] text-muted">{archived.length}</span>
          </h2>
          <div className="overflow-hidden rounded-control border border-border bg-white">
            {archived.map((injury) => (
              <InjuryCard key={injury.id} injury={shape(injury)} showPlayer={false} />
            ))}
          </div>
        </>
      )}

      {adding && <AddInjuryModal defaultPlayerId={playerId} onClose={() => setAdding(false)} />}
    </div>
  );
}

function Overview({
  p,
  externalProfile,
  nhlContract,
  recentLines,
  events,
  sourceRecords,
  onPatch,
}: {
  p: NonNullable<ReturnType<typeof usePlayer>['data']>['player'];
  externalProfile: NonNullable<ReturnType<typeof usePlayer>['data']>['externalProfile'];
  nhlContract: NhlContract | null;
  recentLines: NonNullable<ReturnType<typeof usePlayer>['data']>['recentLines'];
  events: PlayerEvent[];
  sourceRecords: PlayerSourceRecord[];
  onPatch: (field: string, value: string | number | null) => Promise<void>;
}) {
  const age = ageFromDob(p.dob ?? null);
  const prospectAge = age ?? (p.birthYear ? new Date().getFullYear() - p.birthYear : null);
  const saveNumber = (field: string) => (value: string | null) =>
    onPatch(field, parseNullableInt(value));

  return (
    <div className="space-y-6">
      <ProspectTouchPanel playerId={p.id} />
      <EliteProspectsPanel playerId={p.id} />
      <CoveragePanel
        player={p}
        recentLines={recentLines}
        events={events}
        sourceRecords={sourceRecords}
      />

      <div className="rounded-control border border-bartlettGold/40 bg-bartlettGold/5 p-4">
        <SectionHeader>Roster Assignment</SectionHeader>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <CompactEdit label="Current team">
            <EditInPlace
              value={p.currentTeam}
              placeholder="Add current team"
              multiline={false}
              onSave={(v) => onPatch('currentTeam', v)}
            />
          </CompactEdit>
          <CompactEdit label="Current league">
            <EditInPlace
              value={p.currentLeague}
              placeholder="NHL, AHL, NTDP, NCAA..."
              multiline={false}
              onSave={(v) => onPatch('currentLeague', v)}
            />
          </CompactEdit>
          <CompactEdit label="Position">
            <EditInPlace
              value={p.position}
              placeholder="C, LW, RW, D, G"
              multiline={false}
              onSave={(v) => onPatch('position', v)}
            />
          </CompactEdit>
          <CompactEdit label="Jersey">
            <EditInPlace
              value={p.jerseyNumber == null ? null : String(p.jerseyNumber)}
              placeholder="#"
              multiline={false}
              onSave={saveNumber('jerseyNumber')}
            />
          </CompactEdit>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <SectionHeader>Profile</SectionHeader>
          <FieldRow label="Client / prospect">
            <div className="grid gap-2 sm:grid-cols-2">
              <EditInPlace
                value={p.firstName}
                placeholder="First name"
                multiline={false}
                onSave={(v) => {
                  if (v) return onPatch('firstName', v);
                  return Promise.resolve();
                }}
              />
              <EditInPlace
                value={p.lastName}
                placeholder="Last name"
                multiline={false}
                onSave={(v) => {
                  if (v) return onPatch('lastName', v);
                  return Promise.resolve();
                }}
              />
              <EditInPlace
                value={p.displayName}
                placeholder="Display name"
                multiline={false}
                onSave={(v) => onPatch('displayName', v)}
              />
              <EditInPlace
                value={p.preferredName ?? null}
                placeholder="Preferred name"
                multiline={false}
                onSave={(v) => onPatch('preferredName', v)}
              />
            </div>
          </FieldRow>
          <FieldRow label="Born">
            <div className="grid gap-2 sm:grid-cols-2">
              <EditInPlace
                value={p.dob}
                placeholder="DOB YYYY-MM-DD"
                multiline={false}
                onSave={(v) => onPatch('dob', v)}
              />
              <EditInPlace
                value={p.birthYear == null ? null : String(p.birthYear)}
                placeholder="Birth year"
                multiline={false}
                onSave={saveNumber('birthYear')}
              />
              <EditInPlace
                value={p.hometown}
                placeholder="Hometown"
                multiline={false}
                onSave={(v) => onPatch('hometown', v)}
              />
              <EditInPlace
                value={p.nationality}
                placeholder="Nationality"
                multiline={false}
                onSave={(v) => onPatch('nationality', v)}
              />
            </div>
          </FieldRow>
          <FieldRow label="Age / draft">
            <span className="font-mono text-sm">
              {prospectAge != null ? `${prospectAge}y` : '—'}
              {p.draftRights && ` · ${p.draftRights}`}
              {p.prospectPriority != null && ` · P${p.prospectPriority}`}
            </span>
          </FieldRow>
          <FieldRow label="Drafted">
            <EditInPlace
              value={p.draftRights}
              placeholder="Draft rights"
              multiline={false}
              onSave={(v) => onPatch('draftRights', v)}
            />
          </FieldRow>
          <FieldRow label="Contract">
            <EditInPlace
              value={p.contractExpireYear == null ? null : String(p.contractExpireYear)}
              placeholder="Contract expire year"
              multiline={false}
              onSave={saveNumber('contractExpireYear')}
            />
          </FieldRow>
          {nhlContract && (
            <FieldRow label="NHL contract (CapWages)">
              <NhlContractSummary contract={nhlContract} />
            </FieldRow>
          )}
          <FieldRow label="Recent details">
            <EditInPlace
              value={p.recentContractDetails}
              placeholder="Recent contract details from the client master sheet"
              multiline
              onSave={(v) => onPatch('recentContractDetails', v)}
            />
          </FieldRow>
          <FieldRow label="Position">
            <EditInPlace
              value={p.position}
              placeholder="Position"
              multiline={false}
              onSave={(v) => onPatch('position', v)}
            />
          </FieldRow>
          <FieldRow label="Shoots">
            <EditableSelect
              value={p.shoots}
              options={[
                { value: '', label: 'Unknown' },
                { value: 'L', label: 'Left' },
                { value: 'R', label: 'Right' },
              ]}
              onChange={(v) => onPatch('shoots', v || null)}
            />
          </FieldRow>
          <FieldRow label="Height / weight">
            <div className="grid gap-2 sm:grid-cols-2">
              <EditInPlace
                value={p.heightIn == null ? null : String(p.heightIn)}
                placeholder="Height in inches"
                multiline={false}
                onSave={saveNumber('heightIn')}
              />
              <EditInPlace
                value={p.weightLb == null ? null : String(p.weightLb)}
                placeholder="Weight lb"
                multiline={false}
                onSave={saveNumber('weightLb')}
              />
            </div>
          </FieldRow>
          <FieldRow label="Classification">
            <div className="grid gap-2 sm:grid-cols-3">
              <EditableSelect
                value={p.classification}
                options={[
                  { value: 'pro_men', label: 'Pro Men' },
                  { value: 'pro_women', label: 'Pro Women' },
                  { value: 'prospect', label: 'Prospect' },
                ]}
                onChange={(v) => onPatch('classification', v)}
              />
              <EditableSelect
                value={p.status}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'archived', label: 'Archived' },
                ]}
                onChange={(v) => onPatch('status', v)}
              />
              <EditableSelect
                value={p.prospectPriority == null ? '' : String(p.prospectPriority)}
                options={[
                  { value: '', label: 'No priority' },
                  { value: '0', label: 'P0' },
                  { value: '1', label: 'P1' },
                  { value: '2', label: 'P2' },
                ]}
                onChange={(v) => onPatch('prospectPriority', parseNullableInt(v))}
              />
            </div>
          </FieldRow>
          <FieldRow label="External IDs">
            <div className="grid gap-2 sm:grid-cols-2">
              <EditInPlace
                value={p.nhlId == null ? null : String(p.nhlId)}
                placeholder="NHL ID"
                multiline={false}
                onSave={saveNumber('nhlId')}
              />
              <EditInPlace
                value={p.epId == null ? null : String(p.epId)}
                placeholder="Elite Prospects ID"
                multiline={false}
                onSave={saveNumber('epId')}
              />
            </div>
          </FieldRow>
          <FieldRow label="Address">
            <EditInPlace
              value={p.address}
              placeholder="Add contact info, address, parent names, siblings, or secure-file note"
              multiline
              onSave={(v) => onPatch('address', v)}
            />
          </FieldRow>
        </div>

        <div className="space-y-4">
          <SectionHeader>Client Basic Info</SectionHeader>
          <FieldRow label="Cell">
            <EditInPlace
              value={p.cellPhone}
              placeholder="Add cell number"
              onSave={(v) => onPatch('cellPhone', v)}
            />
          </FieldRow>
          <FieldRow label="Primary email">
            <EditInPlace
              value={p.primaryEmail}
              placeholder="Add primary email"
              onSave={(v) => onPatch('primaryEmail', v)}
            />
          </FieldRow>
          <FieldRow label="Secondary email">
            <EditInPlace
              value={p.secondaryEmail}
              placeholder="Add secondary email"
              onSave={(v) => onPatch('secondaryEmail', v)}
            />
          </FieldRow>
          <FieldRow label="In-season mailing">
            <EditInPlace
              value={p.inSeasonMailing}
              placeholder="Add in-season mailing address"
              multiline
              onSave={(v) => onPatch('inSeasonMailing', v)}
            />
          </FieldRow>
          <FieldRow label="Off-season mailing">
            <EditInPlace
              value={p.outOfSeasonMailing}
              placeholder="Add off-season mailing address"
              multiline
              onSave={(v) => onPatch('outOfSeasonMailing', v)}
            />
          </FieldRow>
          <FieldRow label="Spouse / GF">
            <EditInPlace
              value={p.spouseName}
              placeholder="Add spouse or partner name"
              onSave={(v) => onPatch('spouseName', v)}
            />
          </FieldRow>
          <FieldRow label="Parents">
            <EditInPlace
              value={p.parentsNames}
              placeholder="Add parent names"
              onSave={(v) => onPatch('parentsNames', v)}
            />
          </FieldRow>
          <FieldRow label="Kids">
            <div className="grid gap-2 sm:grid-cols-2">
              <EditInPlace value={p.kid1} placeholder="Kid 1" onSave={(v) => onPatch('kid1', v)} />
              <EditInPlace value={p.kid2} placeholder="Kid 2" onSave={(v) => onPatch('kid2', v)} />
              <EditInPlace value={p.kid3} placeholder="Kid 3" onSave={(v) => onPatch('kid3', v)} />
              <EditInPlace value={p.kid4} placeholder="Kid 4" onSave={(v) => onPatch('kid4', v)} />
            </div>
          </FieldRow>
          <FieldRow label="Financial advisor">
            <EditInPlace
              value={p.financialAdvisor}
              placeholder="Add financial advisor"
              onSave={(v) => onPatch('financialAdvisor', v)}
            />
          </FieldRow>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <SectionHeader>Relationship Notes</SectionHeader>
          <FieldRow label="Family / cheat-sheet">
            <EditInPlace
              value={p.cheatSheetNotes}
              placeholder="Parents · siblings · occupations · communication preferences · special handling"
              multiline
              onSave={(v) => onPatch('cheatSheetNotes', v)}
            />
          </FieldRow>
          <FieldRow label="Data source">
            <span className="font-mono text-sm text-muted">
              {p.nhlId ? 'NHL API + Bartlett roster' : 'Bartlett roster'} · updated by{' '}
              {p.updatedBy ?? 'manual'}
            </span>
          </FieldRow>
          <FieldRow label="Secure files">
            <span className="text-sm text-muted">
              W9, banking, contracts, and PDFs will live as access-controlled attachments once the
              R2 file cabinet is enabled.
            </span>
          </FieldRow>
        </div>
      </div>
      <RecentGames
        nhlGameLog={externalProfile?.gameLog?.gameLog ?? []}
        persistedLines={recentLines}
      />
      <FullSeasonForm
        seasonGameLog={externalProfile?.seasonGameLog ?? null}
        persistedLines={recentLines}
      />
      <SeasonSnapshot featuredStats={externalProfile?.landing?.featuredStats ?? null} />
    </div>
  );
}

function CoveragePanel({
  player,
  recentLines,
  events,
  sourceRecords,
}: {
  player: NonNullable<ReturnType<typeof usePlayer>['data']>['player'];
  recentLines: RecentLine[];
  events: PlayerEvent[];
  sourceRecords: PlayerSourceRecord[];
}) {
  const profileSource = sourceRecords.find((source) => source.kind === 'profile');
  const profileSourceDetails = parseSourceRecordDetails(profileSource?.detailsJson);
  const profileBlocked = profileSourceDetails?.status === 'blocked';
  const mediaSource = sourceRecords.find((source) => source.kind === 'media');
  const statsSource = sourceRecords.find((source) => source.kind === 'stats');
  const statsSourceDetails = parseSourceRecordDetails(statsSource?.detailsJson);
  const latestLine = recentLines[0] ?? null;
  const videoEvents = events.filter(
    (event) =>
      event.type === 'video' && Boolean(parsePlayerVideoMetadata(event.metadataJson)?.mediaUrl)
  );
  const identitySource = sourceRecords.find((source) => source.kind === 'identity');
  const cards = [
    {
      label: 'Identity',
      ready: Boolean(identitySource || player.nhlId || player.epId),
      detail:
        identitySource?.provider ??
        (player.nhlId ? 'NHL ID verified' : player.epId ? 'EP ID' : 'Needs source'),
      observedAt: identitySource?.observedAt ?? null,
      url: identitySource?.sourceUrl ?? null,
    },
    {
      label: 'Profile',
      ready: Boolean((profileSource && !profileBlocked) || player.nhlId),
      detail: profileBlocked
        ? String(profileSourceDetails?.blockedReason ?? 'Official source checked; unresolved')
        : (profileSource?.provider ??
          (player.nhlId ? 'Official NHL profile' : 'Needs official profile')),
      observedAt: profileSource?.observedAt ?? player.mediaCheckedAt,
      url: profileSource?.sourceUrl ?? player.mediaSourceUrl,
    },
    {
      label: 'Photo',
      ready: Boolean(player.headshotUrl || player.heroImageUrl),
      detail:
        mediaSource?.provider ??
        (player.mediaSourceUrl ? 'Official NHL media' : 'Needs approved image'),
      observedAt: mediaSource?.observedAt ?? player.mediaCheckedAt,
      url: mediaSource?.sourceUrl ?? player.mediaSourceUrl,
    },
    {
      label: 'Stats',
      ready: Boolean(latestLine || statsSource),
      detail: latestLine
        ? `${latestLine.source} · through ${latestLine.gameDate}`
        : statsSource
          ? formatOfficialStatSummary(statsSource.provider, statsSourceDetails)
          : 'No saved game rows or verified season summary',
      observedAt: latestLine?.ingestedAt ?? statsSource?.observedAt ?? null,
      url: latestLine ? null : (statsSource?.sourceUrl ?? null),
    },
    {
      label: 'Video',
      ready: videoEvents.length > 0,
      detail:
        videoEvents.length > 0
          ? `${videoEvents.length} linked clip${videoEvents.length === 1 ? '' : 's'}`
          : 'No linked clips',
      observedAt: videoEvents[0]?.updatedAt ?? null,
      url: null,
    },
  ];

  return (
    <div className="rounded-control border border-border bg-white p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <SectionHeader>Coverage &amp; Freshness</SectionHeader>
        <span className="font-mono text-[11px] text-muted">
          {cards.filter((card) => card.ready).length}/{cards.length} ready
        </span>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        {cards.map((card) => {
          const content = (
            <>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[11px] font-semibold uppercase tracking-wide text-muted">
                  {card.label}
                </span>
                <span
                  className={`h-2 w-2 rounded-full ${card.ready ? 'bg-flag-ok' : 'bg-flag-warn'}`}
                  aria-label={card.ready ? 'Ready' : 'Coverage gap'}
                />
              </div>
              <div className="mt-1 text-[12px] font-semibold text-ink">{card.detail}</div>
              <div className="mt-1 font-mono text-[10px] text-muted">
                {card.observedAt
                  ? `Checked ${formatObservedAt(card.observedAt)}`
                  : 'Not yet verified'}
              </div>
            </>
          );
          return card.url ? (
            <a
              key={card.label}
              href={card.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-control border border-border bg-bg/40 p-3 hover:border-bartlettGold/60"
            >
              {content}
            </a>
          ) : (
            <div key={card.label} className="rounded-control border border-border bg-bg/40 p-3">
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function parseSourceRecordDetails(
  value: string | null | undefined
): Record<string, unknown> | null {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function formatOfficialStatSummary(
  provider: string,
  details: Record<string, unknown> | null
): string {
  const season = typeof details?.season === 'string' ? details.season : null;
  const gamesPlayed = typeof details?.gamesPlayed === 'number' ? details.gamesPlayed : null;
  const goals = typeof details?.goals === 'number' ? details.goals : null;
  const assists = typeof details?.assists === 'number' ? details.assists : null;
  const points = typeof details?.points === 'number' ? details.points : null;
  if (season && gamesPlayed != null && goals != null && assists != null && points != null) {
    return `${provider} · ${season}: ${gamesPlayed} GP, ${goals} G, ${assists} A, ${points} P`;
  }
  return `${provider} · verified season summary`;
}

function formatObservedAt(value: number): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value * 1000));
}

function CompactEdit({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block font-mono text-[11px] uppercase tracking-wide text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

function EditableSelect({
  value,
  options,
  onChange,
}: {
  value: string | null;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => Promise<void> | void;
}) {
  return (
    <select
      value={value ?? ''}
      onChange={(event) => {
        void onChange(event.target.value);
      }}
      className="focus-ring h-10 w-full rounded-control border border-border bg-white px-2 text-sm text-ink"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function parseNullableInt(value: string | null): number | null {
  if (value == null || value.trim() === '') return null;
  const parsed = Number.parseInt(value.trim(), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

type SeasonGameLog = NonNullable<
  NonNullable<ReturnType<typeof usePlayer>['data']>['externalProfile']
>['seasonGameLog'];

function FullSeasonForm({
  seasonGameLog,
  persistedLines,
}: {
  seasonGameLog: SeasonGameLog | null;
  persistedLines: NonNullable<ReturnType<typeof usePlayer>['data']>['recentLines'];
}) {
  const games =
    seasonGameLog?.gameLog ??
    persistedLines
      .filter((line) => line.source === 'nhl')
      .map((line) => ({
        gameId: Number(line.sourceGameId ?? 0),
        gameDate: line.gameDate,
        teamAbbrev: line.team,
        opponentAbbrev: line.opponent ?? '',
        homeRoadFlag: line.isHome === 0 ? ('R' as const) : ('H' as const),
        goals: line.goals ?? 0,
        assists: line.assists ?? 0,
        points: line.points ?? 0,
        plusMinus: line.plusMinus ?? undefined,
        shots: line.shots ?? 0,
        shifts: 0,
        toi: line.toiSeconds == null ? undefined : formatToiSeconds(line.toiSeconds),
      }));
  if (games.length === 0) return null;

  const last5 = summarizeGames(games.slice(0, 5));
  const last10 = summarizeGames(games.slice(0, 10));
  const full = summarizeGames(games);
  const latest = games[0];
  const seasonLabel = seasonGameLog ? formatNhlSeason(seasonGameLog.seasonId) : 'Persisted NHL';

  return (
    <div className="space-y-3 rounded-control border border-border bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-3">
        <div>
          <SectionHeader>Full Season Form</SectionHeader>
          <p className="mt-2 text-sm text-muted">
            Fast talking points from the full NHL regular-season game log.
          </p>
        </div>
        <span className="shrink-0 rounded-full border border-bartlettGold/40 bg-bartlettGold/10 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-bartlett">
          {seasonLabel}
        </span>
      </div>
      <div className="grid gap-3 lg:grid-cols-[1.1fr_1fr_1fr]">
        <div className="rounded-control border border-bartlett/10 bg-bartlettIce p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-bartlett">
              Scout brief
            </div>
            <TrendPill label={formLabel(last5, full)} />
          </div>
          <div className="space-y-3 text-sm">
            <BriefLine label="Last 5" stats={last5} />
            <BriefLine label="Last 10" stats={last10} />
            <BriefLine label="Season" stats={full} />
            {latest && (
              <div className="rounded-control border border-border/80 bg-white px-3 py-2 text-[13px] text-muted">
                Most recent: <span className="font-medium text-ink">{latest.gameDate}</span>{' '}
                {latest.homeRoadFlag === 'R' ? '@' : 'vs'} {latest.opponentAbbrev} ·{' '}
                {latest.points ?? 0} P · {latest.shots ?? 0} SOG · {latest.toi ?? '—'} TOI
              </div>
            )}
          </div>
        </div>
        <SeasonFormCard title="Last 5" stats={last5} />
        <SeasonFormCard title="Last 10" stats={last10} />
      </div>
    </div>
  );
}

function SeasonFormCard({
  title,
  stats,
}: {
  title: string;
  stats: ReturnType<typeof summarizeGames>;
}) {
  return (
    <div className="rounded-control border border-border bg-white p-4">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">
            {title}
          </div>
          <div className="mt-1 text-sm text-muted">{stats.games} games</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-3xl font-semibold tabular-nums text-ink">
            {stats.points}
          </div>
          <div className="font-mono text-[10px] uppercase text-muted">points</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <MiniStat label="G" value={stats.goals} />
        <MiniStat label="A" value={stats.assists} />
        <MiniStat label="+/-" value={stats.plusMinus} />
        <MiniStat label="SOG" value={stats.shots} />
        <MiniStat label="P/GP" value={stats.pointsPerGame} />
        <MiniStat label="S/GP" value={stats.shotsPerGame} />
        <MiniStat label="TOI" value={stats.averageToi} />
      </div>
    </div>
  );
}

function BriefLine({ label, stats }: { label: string; stats: ReturnType<typeof summarizeGames> }) {
  return (
    <div className="grid grid-cols-[72px_1fr] items-baseline gap-3">
      <div className="font-mono text-[11px] font-semibold uppercase text-muted">{label}</div>
      <div className="min-w-0 text-sm">
        <span className="font-semibold text-ink">{stats.points} P</span>
        <span className="text-muted">
          {' '}
          · {stats.pointsPerGame} P/GP · {stats.shotsPerGame} S/GP · {stats.averageToi} TOI
        </span>
      </div>
    </div>
  );
}

function TrendPill({ label }: { label: string }) {
  return (
    <span className="rounded-full border border-border bg-white px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-muted">
      {label}
    </span>
  );
}

function SeasonSnapshot({
  featuredStats,
}: {
  featuredStats: NonNullable<
    NonNullable<ReturnType<typeof usePlayer>['data']>['externalProfile']
  >['landing'] extends infer T
    ? T extends { featuredStats?: infer F }
      ? F | null
      : null
    : null;
}) {
  const regular = featuredStats?.regularSeason?.subSeason;
  const career = featuredStats?.regularSeason?.career;
  const playoffs = featuredStats?.playoffs?.subSeason;
  if (!regular && !career && !playoffs) return null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <SectionHeader>Season Snapshot</SectionHeader>
        <span className="rounded-full border border-bartlettGold/40 bg-bartlettGold/10 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-bartlett">
          NHL API
        </span>
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        {regular && (
          <StatSummaryCard
            title={`${featuredStats?.season ?? 'Current'} Regular`}
            stats={regular}
          />
        )}
        {playoffs && <StatSummaryCard title="Playoffs" stats={playoffs} />}
        {career && <StatSummaryCard title="Career Regular" stats={career} />}
      </div>
    </div>
  );
}

function StatSummaryCard({ title, stats }: { title: string; stats: SeasonStatSummary }) {
  return (
    <div className="rounded-control border border-border bg-white p-4 shadow-sm">
      <div className="mb-3 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">
        {title}
      </div>
      <div className="grid grid-cols-4 gap-y-3">
        <MiniStat label="GP" value={stats.gamesPlayed ?? '—'} />
        <MiniStat label="G" value={stats.goals ?? '—'} />
        <MiniStat label="A" value={stats.assists ?? '—'} />
        <MiniStat label="P" value={stats.points ?? '—'} />
        <MiniStat label="+/-" value={stats.plusMinus ?? '—'} />
        <MiniStat label="PIM" value={stats.pim ?? '—'} />
        <MiniStat label="SOG" value={stats.shots ?? '—'} />
        <MiniStat label="S%" value={formatPct(stats.shootingPctg)} />
      </div>
    </div>
  );
}

function RecentGames({
  nhlGameLog,
  persistedLines,
}: {
  nhlGameLog: NonNullable<
    NonNullable<ReturnType<typeof usePlayer>['data']>['externalProfile']
  >['gameLog'] extends infer T
    ? T extends { gameLog: infer G }
      ? G
      : never
    : never;
  persistedLines: NonNullable<ReturnType<typeof usePlayer>['data']>['recentLines'];
}) {
  const games = Array.isArray(nhlGameLog) ? nhlGameLog.slice(0, 5) : [];
  const sourceLabel = persistedLines.some((game) => game.source === 'nhl')
    ? 'Persisted NHL API'
    : games.length > 0
      ? 'Live NHL API'
      : persistedLines.some((game) => game.source === 'espn')
        ? 'Persisted ESPN'
        : 'Persisted source';
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <SectionHeader>Recent Games</SectionHeader>
        {(games.length > 0 || persistedLines.length > 0) && (
          <span className="rounded-full border border-bartlettGold/40 bg-bartlettGold/10 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-bartlett">
            {sourceLabel}
          </span>
        )}
      </div>
      {games.length === 0 && persistedLines.length === 0 ? (
        <EmptyState text="No recent game log is available for this player yet." />
      ) : games.length > 0 ? (
        <div className="overflow-hidden rounded-control border border-border bg-white">
          {games.map((game) => (
            <div
              key={game.gameId}
              className="grid grid-cols-[1fr_auto] gap-3 border-b border-border px-4 py-3 last:border-b-0 sm:grid-cols-[1fr_repeat(5,64px)]"
            >
              <div className="min-w-0">
                <div className="font-mono text-[12px] text-muted">{game.gameDate}</div>
                <div className="text-sm font-semibold">
                  {game.homeRoadFlag === 'R' ? '@' : 'vs'} {game.opponentAbbrev}
                </div>
              </div>
              <MiniStat label="G" value={game.goals ?? 0} />
              <MiniStat label="A" value={game.assists ?? 0} />
              <MiniStat label="P" value={game.points ?? 0} />
              <MiniStat label="SOG" value={game.shots ?? 0} />
              <MiniStat label="TOI" value={game.toi ?? '—'} />
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-control border border-border bg-white">
          {persistedLines.slice(0, 5).map((game) => (
            <div
              key={game.id}
              className="grid grid-cols-[1fr_auto] gap-3 border-b border-border px-4 py-3 last:border-b-0 sm:grid-cols-[1fr_repeat(5,64px)]"
            >
              <div className="min-w-0">
                <div className="font-mono text-[12px] text-muted">{game.gameDate}</div>
                <div className="text-sm font-semibold">
                  {game.team}{' '}
                  {game.opponent ? `${game.isHome === 0 ? '@' : 'vs'} ${game.opponent}` : ''}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 font-mono text-[11px] text-muted">
                  <span>{game.league}</span>
                  <span>{game.source.toUpperCase()}</span>
                  {game.teamScore != null && game.opponentScore != null && (
                    <span>
                      {game.teamScore}-{game.opponentScore} {game.result ?? ''}
                    </span>
                  )}
                </div>
              </div>
              <MiniStat label="G" value={game.goals ?? 0} />
              <MiniStat label="A" value={game.assists ?? 0} />
              <MiniStat label="P" value={game.points ?? 0} />
              <MiniStat label="RES" value={game.result ?? '—'} />
              <MiniStat label="TOI" value={formatToiSeconds(game.toiSeconds)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type PlayerStatSortKey = 'date' | 'points' | 'goals' | 'assists' | 'shots' | 'toi';

const PLAYER_STAT_SORTS: Array<{ key: PlayerStatSortKey; label: string }> = [
  { key: 'date', label: 'Date' },
  { key: 'points', label: 'Pts' },
  { key: 'goals', label: 'G' },
  { key: 'assists', label: 'A' },
  { key: 'shots', label: 'SOG' },
  { key: 'toi', label: 'TOI' },
];

function StatsSubTab({ recentLines }: { recentLines: RecentLine[] }) {
  const [sortKey, setSortKey] = useState<PlayerStatSortKey>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const playedLines = useMemo(() => recentLines.filter((line) => line.isDnp !== 1), [recentLines]);
  const career = useMemo(() => summarizePersistedLines(playedLines), [playedLines]);
  const seasons = useMemo(() => summarizeSeasons(playedLines), [playedLines]);
  const records = useMemo(() => playerRecords(playedLines), [playedLines]);
  const sortedLines = useMemo(
    () => sortPlayerLines(playedLines, sortKey, sortDirection),
    [playedLines, sortDirection, sortKey]
  );

  function updateSort(nextKey: PlayerStatSortKey) {
    if (nextKey === sortKey) {
      setSortDirection((current) => (current === 'desc' ? 'asc' : 'desc'));
      return;
    }
    setSortKey(nextKey);
    setSortDirection('desc');
  }

  if (recentLines.length === 0) {
    return <EmptyState text="No saved game history is available for this player yet." />;
  }

  return (
    <div className="space-y-5">
      <section className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <SectionHeader>Career Saved Stats</SectionHeader>
          <span className="font-mono text-[11px] text-muted">{playedLines.length} saved games</span>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
          <StatTile label="GP" value={career.games} />
          <StatTile label="G" value={career.goals} />
          <StatTile label="A" value={career.assists} />
          <StatTile label="P" value={career.points} />
          <StatTile label="P/GP" value={career.pointsPerGame} />
          <StatTile label="SOG" value={career.shots} />
          <StatTile label="PIM" value={career.pim} />
          <StatTile label="HIT" value={career.hits} />
          <StatTile label="BLK" value={career.blocked} />
          <StatTile label="GVA" value={career.giveaways} />
          <StatTile label="TKA" value={career.takeaways} />
          <StatTile label="TOI" value={career.averageToi} />
          {career.saves > 0 && <StatTile label="SV" value={career.saves} />}
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-[1fr_0.85fr]">
        <div className="space-y-3">
          <SectionHeader>Season Records</SectionHeader>
          {seasons.length === 0 ? (
            <EmptyState text="No season summaries are available yet." />
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {seasons.map((season) => (
                <div
                  key={season.key}
                  className="rounded-control border border-border bg-white p-4 shadow-sm"
                >
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-ink">{season.label}</div>
                      <div className="mt-0.5 font-mono text-[11px] uppercase text-muted">
                        {season.league} · {season.source.toUpperCase()}
                      </div>
                    </div>
                    <span className="rounded-full border border-border bg-bg px-2 py-0.5 font-mono text-[10px] text-muted">
                      {season.games} GP
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-y-3">
                    <MiniStat label="G" value={season.goals} />
                    <MiniStat label="A" value={season.assists} />
                    <MiniStat label="P" value={season.points} />
                    <MiniStat label="P/GP" value={season.pointsPerGame} />
                    <MiniStat label="SOG" value={season.shots} />
                    <MiniStat label="+/-" value={formatPlus(season.plusMinus)} />
                    <MiniStat label="PIM" value={season.pim} />
                    <MiniStat label="TOI" value={season.averageToi} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <SectionHeader>Single-Game Highs</SectionHeader>
          <div className="space-y-2 rounded-control border border-border bg-white p-3 shadow-sm">
            <RecordLine label="Points" record={records.points} value={(line) => num(line.points)} />
            <RecordLine label="Goals" record={records.goals} value={(line) => num(line.goals)} />
            <RecordLine
              label="Assists"
              record={records.assists}
              value={(line) => num(line.assists)}
            />
            <RecordLine label="Shots" record={records.shots} value={(line) => num(line.shots)} />
            <RecordLine
              label="TOI"
              record={records.toi}
              value={(line) => formatToiSeconds(line.toiSeconds)}
            />
            <RecordLine label="Saves" record={records.saves} value={(line) => num(line.saves)} />
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SectionHeader>Individual Game Log</SectionHeader>
          <div className="flex max-w-full gap-1 overflow-x-auto">
            {PLAYER_STAT_SORTS.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => updateSort(option.key)}
                className={[
                  'focus-ring inline-flex h-8 shrink-0 items-center gap-1 rounded-full border px-2.5 font-mono text-[11px] font-semibold',
                  sortKey === option.key
                    ? 'border-bartlett bg-bartlett text-white'
                    : 'border-border bg-white text-muted hover:border-bartlett/40',
                ].join(' ')}
              >
                <span>{option.label}</span>
                {sortKey === option.key && (
                  <span className="text-[9px] uppercase opacity-80">{sortDirection}</span>
                )}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-hidden rounded-control border border-border bg-white shadow-sm">
          {sortedLines.map((line) => (
            <PlayerGameLogRow key={line.id} line={line} />
          ))}
        </div>
      </section>
    </div>
  );
}

function PlayerGameLogRow({ line }: { line: RecentLine }) {
  const isGoalie = line.decision != null || line.saves != null || line.shotsAgainst != null;
  return (
    <div className="grid gap-3 border-b border-border px-3 py-3 last:border-b-0 lg:grid-cols-[minmax(190px,1fr)_minmax(160px,0.8fr)_minmax(320px,1.2fr)]">
      <div className="min-w-0">
        <div className="font-mono text-[12px] font-semibold text-bartlett">{line.gameDate}</div>
        <div className="mt-0.5 truncate text-sm font-semibold text-ink">
          {line.team} {line.opponent ? `${line.isHome === 0 ? '@' : 'vs'} ${line.opponent}` : ''}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-2 font-mono text-[11px] uppercase text-muted">
          <span>{line.league}</span>
          <span>{line.source}</span>
          {line.teamScore != null && line.opponentScore != null && (
            <span>
              {line.teamScore}-{line.opponentScore} {line.result ?? ''}
            </span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-5 gap-2">
        <MiniStat label="G" value={line.goals ?? 0} />
        <MiniStat label="A" value={line.assists ?? 0} />
        <MiniStat label="P" value={line.points ?? 0} />
        <MiniStat label="SOG" value={line.shots ?? '—'} />
        <MiniStat label="+/-" value={formatPlus(line.plusMinus)} />
      </div>
      <div className="grid grid-cols-5 gap-2">
        {isGoalie ? (
          <>
            <MiniStat label="DEC" value={line.decision ?? '—'} />
            <MiniStat label="SV" value={line.saves ?? '—'} />
            <MiniStat label="SA" value={line.shotsAgainst ?? '—'} />
            <MiniStat label="GA" value={line.goalsAgainst ?? '—'} />
            <MiniStat label="SV%" value={formatDecimalPct(line.savePct)} />
          </>
        ) : (
          <>
            <MiniStat label="PIM" value={line.pim ?? '—'} />
            <MiniStat label="HIT" value={line.hits ?? '—'} />
            <MiniStat label="BLK" value={line.blocked ?? '—'} />
            <MiniStat label="GVA" value={line.giveaways ?? '—'} />
            <MiniStat label="TKA" value={line.takeaways ?? '—'} />
          </>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-control border border-border bg-white p-3 shadow-sm">
      <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
        {label}
      </div>
      <div className="mt-1 font-mono text-2xl font-semibold tabular-nums text-ink">{value}</div>
    </div>
  );
}

function RecordLine({
  label,
  record,
  value,
}: {
  label: string;
  record: RecentLine | null;
  value: (line: RecentLine) => string;
}) {
  return (
    <div className="grid grid-cols-[74px_52px_1fr] items-center gap-3 rounded-control border border-border/70 bg-bg px-3 py-2">
      <span className="font-mono text-[11px] font-semibold uppercase text-muted">{label}</span>
      <span className="font-mono text-sm font-semibold tabular-nums text-ink">
        {record ? value(record) : '—'}
      </span>
      <span className="truncate text-sm text-muted">
        {record ? recordLabel(record) : 'No record yet'}
      </span>
    </div>
  );
}

function summarizePersistedLines(lines: RecentLine[]) {
  const totals = totalsForLines(lines);
  return {
    ...totals,
    pointsPerGame: totals.games > 0 ? (totals.points / totals.games).toFixed(2) : '0.00',
    averageToi:
      totals.toiGames > 0 ? formatToiSeconds(Math.round(totals.toiSeconds / totals.toiGames)) : '—',
  };
}

function summarizeSeasons(lines: RecentLine[]) {
  const groups = new Map<string, RecentLine[]>();
  for (const line of lines) {
    const season = hockeySeasonLabel(line.gameDate);
    const key = `${season}__${line.league}__${line.source}`;
    groups.set(key, [...(groups.get(key) ?? []), line]);
  }
  return [...groups.entries()]
    .map(([key, items]) => {
      const totals = summarizePersistedLines(items);
      const [label = 'Unknown', league = '—', source = 'saved'] = key.split('__');
      return { key, label, league, source, ...totals };
    })
    .sort((a, b) => b.label.localeCompare(a.label) || a.league.localeCompare(b.league));
}

function totalsForLines(lines: RecentLine[]) {
  return lines.reduce(
    (acc, line) => ({
      games: acc.games + 1,
      goals: acc.goals + (line.goals ?? 0),
      assists: acc.assists + (line.assists ?? 0),
      points: acc.points + (line.points ?? 0),
      plusMinus: acc.plusMinus + (line.plusMinus ?? 0),
      pim: acc.pim + (line.pim ?? 0),
      shots: acc.shots + (line.shots ?? 0),
      hits: acc.hits + (line.hits ?? 0),
      blocked: acc.blocked + (line.blocked ?? 0),
      giveaways: acc.giveaways + (line.giveaways ?? 0),
      takeaways: acc.takeaways + (line.takeaways ?? 0),
      saves: acc.saves + (line.saves ?? 0),
      shotsAgainst: acc.shotsAgainst + (line.shotsAgainst ?? 0),
      goalsAgainst: acc.goalsAgainst + (line.goalsAgainst ?? 0),
      shutouts: acc.shutouts + (line.shutout ?? 0),
      toiSeconds: acc.toiSeconds + (line.toiSeconds ?? 0),
      toiGames: acc.toiGames + (line.toiSeconds != null ? 1 : 0),
    }),
    {
      games: 0,
      goals: 0,
      assists: 0,
      points: 0,
      plusMinus: 0,
      pim: 0,
      shots: 0,
      hits: 0,
      blocked: 0,
      giveaways: 0,
      takeaways: 0,
      saves: 0,
      shotsAgainst: 0,
      goalsAgainst: 0,
      shutouts: 0,
      toiSeconds: 0,
      toiGames: 0,
    }
  );
}

function playerRecords(lines: RecentLine[]) {
  return {
    points: maxLine(lines, (line) => line.points),
    goals: maxLine(lines, (line) => line.goals),
    assists: maxLine(lines, (line) => line.assists),
    shots: maxLine(lines, (line) => line.shots),
    toi: maxLine(lines, (line) => line.toiSeconds),
    saves: maxLine(lines, (line) => line.saves),
  };
}

function maxLine(
  lines: RecentLine[],
  value: (line: RecentLine) => number | null | undefined
): RecentLine | null {
  let best: RecentLine | null = null;
  let bestValue = Number.NEGATIVE_INFINITY;
  for (const line of lines) {
    const next = value(line);
    if (next == null) continue;
    if (next > bestValue) {
      best = line;
      bestValue = next;
    }
  }
  return best;
}

function sortPlayerLines(
  lines: RecentLine[],
  sortKey: PlayerStatSortKey,
  direction: 'asc' | 'desc'
) {
  return [...lines].sort((a, b) => {
    const result = comparePlayerSortValue(playerSortValue(a, sortKey), playerSortValue(b, sortKey));
    if (result !== 0) return direction === 'asc' ? result : -result;
    return b.gameDate.localeCompare(a.gameDate);
  });
}

function playerSortValue(line: RecentLine, sortKey: PlayerStatSortKey): string | number | null {
  switch (sortKey) {
    case 'date':
      return line.gameDate;
    case 'points':
      return line.points;
    case 'goals':
      return line.goals;
    case 'assists':
      return line.assists;
    case 'shots':
      return line.shots;
    case 'toi':
      return line.toiSeconds;
  }
}

function comparePlayerSortValue(a: string | number | null, b: string | number | null): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function hockeySeasonLabel(value: string): string {
  const [yearRaw, monthRaw] = value.split('-');
  const year = Number.parseInt(yearRaw ?? '', 10);
  const month = Number.parseInt(monthRaw ?? '', 10);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return 'Unknown';
  const start = month >= 8 ? year : year - 1;
  return `${start}-${String(start + 1).slice(2)}`;
}

function recordLabel(line: RecentLine): string {
  const opponent = line.opponent ? `${line.isHome === 0 ? '@' : 'vs'} ${line.opponent}` : line.team;
  return `${line.gameDate} · ${opponent} · ${line.league}`;
}

function num(value: number | null | undefined): string {
  return value == null ? '—' : String(value);
}

function formatPlus(value: number | null | undefined): string {
  if (value == null) return '—';
  return value > 0 ? `+${value}` : String(value);
}

function formatDecimalPct(value: number | null | undefined): string {
  if (value == null) return '—';
  return value <= 1 ? value.toFixed(3).replace(/^0/, '') : value.toFixed(3);
}

function formatToiSeconds(value: number | null): string {
  if (value == null) return '—';
  const minutes = Math.floor(value / 60);
  const seconds = value % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

type SeasonGame = NonNullable<SeasonGameLog>['gameLog'][number];

function summarizeGames(games: SeasonGame[]) {
  const totals = games.reduce(
    (acc, game) => ({
      goals: acc.goals + (game.goals ?? 0),
      assists: acc.assists + (game.assists ?? 0),
      points: acc.points + (game.points ?? 0),
      plusMinus: acc.plusMinus + (game.plusMinus ?? 0),
      shots: acc.shots + (game.shots ?? 0),
      shifts: acc.shifts + (game.shifts ?? 0),
      toiSeconds: acc.toiSeconds + parseToi(game.toi),
      toiGames: acc.toiGames + (game.toi ? 1 : 0),
    }),
    {
      goals: 0,
      assists: 0,
      points: 0,
      plusMinus: 0,
      shots: 0,
      shifts: 0,
      toiSeconds: 0,
      toiGames: 0,
    }
  );
  return {
    games: games.length,
    goals: totals.goals,
    assists: totals.assists,
    points: totals.points,
    plusMinus: totals.plusMinus,
    shots: totals.shots,
    shifts: totals.shifts,
    pointsPerGame: games.length > 0 ? (totals.points / games.length).toFixed(2) : '0.00',
    shotsPerGame: games.length > 0 ? (totals.shots / games.length).toFixed(1) : '0.0',
    averageToi:
      totals.toiGames > 0 ? formatToiSeconds(Math.round(totals.toiSeconds / totals.toiGames)) : '—',
  };
}

function formLabel(
  recent: ReturnType<typeof summarizeGames>,
  season: ReturnType<typeof summarizeGames>
): string {
  const recentRate = Number.parseFloat(recent.pointsPerGame);
  const seasonRate = Number.parseFloat(season.pointsPerGame);
  if (!Number.isFinite(recentRate) || !Number.isFinite(seasonRate)) return 'form';
  if (recentRate >= seasonRate + 0.2) return 'trending up';
  if (recentRate <= seasonRate - 0.2) return 'watch';
  return 'steady';
}

function parseToi(value: string | undefined): number {
  if (!value) return 0;
  const parts = value.split(':').map((part) => Number.parseInt(part, 10));
  const minutes = parts[0];
  const seconds = parts[1];
  if (minutes == null || seconds == null) return 0;
  if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return 0;
  return minutes * 60 + seconds;
}

function formatNhlSeason(seasonId: number): string {
  const raw = String(seasonId);
  if (raw.length !== 8) return raw;
  return `${raw.slice(0, 4)}-${raw.slice(6)}`;
}

function formatPct(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function MiniStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="text-right">
      <div className="font-mono text-[10px] uppercase text-muted">{label}</div>
      <div className="font-mono text-sm tabular-nums">{value}</div>
    </div>
  );
}

function TimelineSubTab({ playerId, events }: { playerId: string; events: PlayerEvent[] }) {
  return (
    <CabinetPanel
      title="Chronological Notes"
      playerId={playerId}
      eventType="note"
      events={events}
      actionLabel="New note"
      icon={<MessageSquarePlus size={15} />}
      empty="No call, text, or meeting notes have been logged for this player yet."
      titlePlaceholder="Call with player, parent text, meeting note"
      bodyPlaceholder="What happened, follow-up, tone, asks, and next step"
    />
  );
}

function EndorsementsSubTab({
  p,
  onPatch,
}: {
  p: NonNullable<ReturnType<typeof usePlayer>['data']>['player'];
  onPatch: (field: string, value: string | null) => Promise<void>;
}) {
  const hasNotes = Boolean(p.endorsementsText?.trim());
  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-3">
        <SkeletonMetric
          icon={CircleDollarSign}
          label="Active deals"
          value={hasNotes ? 'review' : '0'}
        />
        <SkeletonMetric icon={ClipboardCheck} label="Open obligations" value="tracked here" />
        <SkeletonMetric icon={FileText} label="Contracts" value="R2 vault" />
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-4">
          <SectionHeader>Deal Ledger</SectionHeader>
          <FieldRow label="Current endorsement notes">
            <EditInPlace
              value={p.endorsementsText}
              placeholder="Deal, payment due, payment made, contract link, deliverables, social metrics"
              multiline
              onSave={(v) => onPatch('endorsementsText', v)}
            />
          </FieldRow>
          {!hasNotes && <EmptyState text="No endorsement records have been logged yet." />}
        </div>

        <div className="space-y-3">
          <SectionHeader>Operating Checklist</SectionHeader>
          <ChecklistItem
            title="Payment timing"
            body="Amounts due, paid dates, and follow-up owner."
          />
          <ChecklistItem
            title="Deliverables"
            body="Appearances, posts, equipment obligations, and approval status."
          />
          <ChecklistItem
            title="Contract file"
            body="Attach signed agreement and W9/banking references through the secure file layer."
          />
          <ChecklistItem
            title="Social metrics"
            body="Engagement and audience notes when a brand asks for campaign proof."
          />
        </div>
      </div>
    </div>
  );
}

function ViewingsSubTab({ playerId, events }: { playerId: string; events: PlayerEvent[] }) {
  const viewingEvents = events.filter((event) => event.type === 'viewing');

  return (
    <div className="space-y-5">
      <CabinetPanel
        title="Viewing Cadence"
        playerId={playerId}
        eventType="viewing"
        events={viewingEvents}
        actionLabel="Log viewing"
        icon={<CalendarDays size={15} />}
        empty="No viewing records have been logged for this player yet."
        titlePlaceholder="Watched vs opponent, practice, showcase"
        bodyPlaceholder="Viewing notes, evaluator, clips to review, and next viewing target"
        dateLabel="Viewing date"
      />
      <ViewingTimeline events={viewingEvents} />
    </div>
  );
}

function ViewingTimeline({ events }: { events: PlayerEvent[] }) {
  if (events.length === 0) return null;

  return (
    <div className="space-y-3">
      <SectionHeader>Viewing Timeline</SectionHeader>
      <div className="rounded-control border border-border bg-white p-4 shadow-sm">
        <div className="space-y-4">
          {events.map((event) => (
            <div
              key={`viewing-timeline-${event.id}`}
              className="grid gap-3 sm:grid-cols-[120px_1fr]"
            >
              <time
                className="font-mono text-[12px] font-semibold text-bartlett"
                dateTime={eventDateIso(event.occurredAt)}
              >
                {formatEventDate(event.occurredAt)}
              </time>
              <div className="border-l border-border pl-4">
                <div className="text-sm font-semibold text-ink">{event.title}</div>
                {event.body && (
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-5 text-muted">
                    {event.body}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BrandingSubTab({
  p,
  onPatch,
}: {
  p: NonNullable<ReturnType<typeof usePlayer>['data']>['player'];
  onPatch: (field: string, value: string | null) => Promise<void>;
}) {
  return (
    <div className="space-y-4">
      <SectionHeader>Branding Sheet</SectionHeader>
      <EditInPlace
        value={p.brandingSheet}
        placeholder="Annual branding exercise: comparable players, positioning, strengths, talking points"
        multiline
        onSave={(v) => onPatch('brandingSheet', v)}
      />
      {!p.brandingSheet && <EmptyState text="No branding sheet has been logged yet." />}
    </div>
  );
}

function VideoSubTab({ playerId, events }: { playerId: string; events: PlayerEvent[] }) {
  return (
    <CabinetPanel
      title="Video Analysis"
      playerId={playerId}
      eventType="video"
      events={events.filter((event) => event.type === 'video')}
      actionLabel="Add clip"
      icon={<Video size={15} />}
      empty="No video analysis clips have been linked for this player yet."
      titlePlaceholder="Clip title or game"
      bodyPlaceholder="Video-coach notes, tags, teaching points, and follow-up"
      supportsMedia
    />
  );
}

function ScheduleSubTab({ playerId, events }: { playerId: string; events: PlayerEvent[] }) {
  const scheduleEvents = events.filter((event) => event.type === 'schedule');
  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-3">
        <SkeletonMetric
          icon={CalendarClock}
          label="Next viewing"
          value={scheduleEvents.length > 0 ? 'logged' : 'none'}
        />
        <SkeletonMetric icon={Route} label="Travel cluster" value="manual" />
        <SkeletonMetric icon={MapPin} label="Client overlap" value="watch" />
      </div>
      <div className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
        <CabinetPanel
          title="Schedule Intelligence"
          playerId={playerId}
          eventType="schedule"
          events={scheduleEvents}
          actionLabel="Add event"
          icon={<CalendarDays size={15} />}
          empty="No player-specific schedule events have been logged yet."
          titlePlaceholder="Client-v-client game, trip, viewing cluster"
          bodyPlaceholder="Location, opponent, timing, travel notes, or follow-up"
        />
        <div className="space-y-3">
          <SectionHeader>Smart Scheduling Targets</SectionHeader>
          <ChecklistItem
            title="Client-v-client games"
            body="Flag games where two Bartlett clients overlap so one viewing covers multiple files."
          />
          <ChecklistItem
            title="Efficient bunches"
            body="Group nearby games, showcases, and practices into one trip plan."
          />
          <ChecklistItem
            title="Last / next viewing"
            body="Keep the running record of when this player was last seen and when they need to be seen next."
          />
          <ChecklistItem
            title="Return-to-play watch"
            body="Coordinate with IR when a player is projected back within seven days."
          />
        </div>
      </div>
    </div>
  );
}

function SkeletonMetric({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof CalendarDays;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-control border border-border bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-muted">
        <Icon size={14} />
        <span className="font-mono text-[10px] uppercase tracking-[0.14em]">{label}</span>
      </div>
      <div className="mt-2 text-sm font-semibold text-ink">{value}</div>
    </div>
  );
}

function ChecklistItem({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-control border border-border bg-white p-3 shadow-sm">
      <div className="text-sm font-semibold text-ink">{title}</div>
      <p className="mt-1 text-[13px] leading-5 text-muted">{body}</p>
    </div>
  );
}

function CabinetPanel({
  title,
  playerId,
  eventType,
  events,
  actionLabel,
  icon,
  empty,
  titlePlaceholder,
  bodyPlaceholder,
  dateLabel,
  supportsMedia = false,
}: {
  title: string;
  playerId: string;
  eventType: PlayerEvent['type'];
  events: PlayerEvent[];
  actionLabel: string;
  icon: ReactNode;
  empty: string;
  titlePlaceholder: string;
  bodyPlaceholder: string;
  dateLabel?: string;
  supportsMedia?: boolean;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [entryTitle, setEntryTitle] = useState('');
  const [body, setBody] = useState('');
  const [occurredDate, setOccurredDate] = useState(todayInputValue());
  const [mediaUrl, setMediaUrl] = useState('');
  const [sourceLabel, setSourceLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editDate, setEditDate] = useState(todayInputValue());
  const [editMediaUrl, setEditMediaUrl] = useState('');
  const [editSourceLabel, setEditSourceLabel] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const scopedEvents =
    eventType === 'note' ? events : events.filter((event) => event.type === eventType);
  const mediaUrlValid = !mediaUrl.trim() || Boolean(normalizeHttpUrl(mediaUrl));
  const editMediaUrlValid = !editMediaUrl.trim() || Boolean(normalizeHttpUrl(editMediaUrl));

  async function saveEvent() {
    const trimmedTitle = entryTitle.trim();
    const trimmedBody = body.trim();
    if (!trimmedTitle || saving || !mediaUrlValid) return;
    setSaving(true);
    try {
      await apiPost(`/api/players/${playerId}/events`, {
        type: eventType,
        title: trimmedTitle,
        body: trimmedBody || null,
        occurredAt: dateInputToUnix(occurredDate),
        ...(supportsMedia
          ? { mediaUrl: mediaUrl.trim() || null, sourceLabel: sourceLabel.trim() || null }
          : {}),
      });
      setEntryTitle('');
      setBody('');
      setOccurredDate(todayInputValue());
      setMediaUrl('');
      setSourceLabel('');
      setAdding(false);
      qc.invalidateQueries({ queryKey: ['player', playerId] });
    } finally {
      setSaving(false);
    }
  }

  function startEdit(event: PlayerEvent) {
    setEditingId(event.id);
    setEditTitle(event.title);
    setEditBody(event.body ?? '');
    setEditDate(dateToInputValue(new Date(event.occurredAt * 1000)));
    const metadata = parsePlayerVideoMetadata(event.metadataJson);
    setEditMediaUrl(metadata?.mediaUrl ?? '');
    setEditSourceLabel(metadata?.sourceLabel ?? '');
  }

  async function saveEdit(event: PlayerEvent) {
    const trimmedTitle = editTitle.trim();
    if (!trimmedTitle || saving || !editMediaUrlValid) return;
    setSaving(true);
    try {
      await apiPatch(`/api/players/${playerId}/events/${event.id}`, {
        title: trimmedTitle,
        body: editBody.trim() || null,
        occurredAt: dateInputToUnix(editDate),
        ...(supportsMedia
          ? { mediaUrl: editMediaUrl.trim() || null, sourceLabel: editSourceLabel.trim() || null }
          : {}),
      });
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ['player', playerId] });
    } finally {
      setSaving(false);
    }
  }

  async function deleteEvent(event: PlayerEvent) {
    const confirmed = window.confirm(`Delete "${event.title}" from this client file?`);
    if (!confirmed) return;
    setDeletingId(event.id);
    try {
      await apiDelete(`/api/players/${playerId}/events/${event.id}`);
      qc.invalidateQueries({ queryKey: ['player', playerId] });
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <SectionHeader>{title}</SectionHeader>
        <button
          type="button"
          onClick={() => setAdding((value) => !value)}
          className="inline-flex h-9 items-center gap-2 rounded-control border border-bartlett bg-bartlett px-3 text-sm font-semibold text-white hover:bg-bartlett/95"
        >
          {icon}
          <span>{actionLabel}</span>
        </button>
      </div>
      {adding && (
        <div className="space-y-3 rounded-control border border-border bg-white p-4 shadow-sm">
          <input
            value={entryTitle}
            onChange={(event) => setEntryTitle(event.target.value)}
            placeholder={titlePlaceholder}
            className="h-10 w-full rounded-control border border-border px-3 text-sm outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10"
          />
          <label className="grid gap-1">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
              {dateLabel ?? 'Date'}
            </span>
            <input
              type="date"
              value={occurredDate}
              onChange={(event) => setOccurredDate(event.target.value)}
              className="h-10 w-full rounded-control border border-border px-3 text-sm outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10 sm:w-56"
            />
          </label>
          <textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder={bodyPlaceholder}
            rows={4}
            className="w-full resize-y rounded-control border border-border px-3 py-2 text-sm outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10"
          />
          {supportsMedia && (
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_180px]">
              <label className="grid gap-1">
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
                  Video link
                </span>
                <input
                  type="url"
                  value={mediaUrl}
                  onChange={(event) => setMediaUrl(event.target.value)}
                  placeholder="https://…"
                  className="h-10 w-full rounded-control border border-border px-3 text-sm outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10"
                />
              </label>
              <label className="grid gap-1">
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
                  Source
                </span>
                <input
                  value={sourceLabel}
                  onChange={(event) => setSourceLabel(event.target.value)}
                  placeholder="Hudl, NHL, YouTube"
                  maxLength={80}
                  className="h-10 w-full rounded-control border border-border px-3 text-sm outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10"
                />
              </label>
            </div>
          )}
          {supportsMedia && !mediaUrlValid && (
            <p className="text-xs text-flag-critical">
              Use a complete http:// or https:// video link.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="h-9 rounded-control border border-border px-3 text-sm font-semibold text-muted hover:text-ink"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveEvent}
              disabled={!entryTitle.trim() || saving || !mediaUrlValid}
              className="h-9 rounded-control border border-bartlett bg-bartlett px-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
      {scopedEvents.length === 0 ? (
        <EmptyState text={empty} />
      ) : (
        <div className="overflow-hidden rounded-control border border-border bg-white">
          {scopedEvents.map((event) => {
            const metadata = parsePlayerVideoMetadata(event.metadataJson);
            const prospectTouch = parseProspectTouchTimelineMetadata(event.metadataJson);
            const legacyUrl = event.body?.startsWith('http') ? normalizeHttpUrl(event.body) : null;
            const openUrl = metadata?.mediaUrl ?? legacyUrl;
            return (
              <div key={event.id} className="border-b border-border px-4 py-3 last:border-b-0">
                {editingId === event.id ? (
                  <div className="space-y-3">
                    <input
                      value={editTitle}
                      onChange={(change) => setEditTitle(change.target.value)}
                      className="h-10 w-full rounded-control border border-border px-3 text-sm outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10"
                    />
                    <input
                      type="date"
                      value={editDate}
                      onChange={(change) => setEditDate(change.target.value)}
                      className="h-10 w-full rounded-control border border-border px-3 text-sm outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10 sm:w-56"
                    />
                    <textarea
                      value={editBody}
                      onChange={(change) => setEditBody(change.target.value)}
                      rows={4}
                      className="w-full resize-y rounded-control border border-border px-3 py-2 text-sm outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10"
                    />
                    {supportsMedia && (
                      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_180px]">
                        <input
                          type="url"
                          value={editMediaUrl}
                          onChange={(change) => setEditMediaUrl(change.target.value)}
                          placeholder="https://…"
                          className="h-10 w-full rounded-control border border-border px-3 text-sm outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10"
                        />
                        <input
                          value={editSourceLabel}
                          onChange={(change) => setEditSourceLabel(change.target.value)}
                          placeholder="Source"
                          maxLength={80}
                          className="h-10 w-full rounded-control border border-border px-3 text-sm outline-none focus:border-bartlett focus:ring-2 focus:ring-bartlett/10"
                        />
                      </div>
                    )}
                    {supportsMedia && !editMediaUrlValid && (
                      <p className="text-xs text-flag-critical">
                        Use a complete http:// or https:// video link.
                      </p>
                    )}
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="h-9 rounded-control border border-border px-3 text-sm font-semibold text-muted hover:text-ink"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => saveEdit(event)}
                        disabled={!editTitle.trim() || saving || !editMediaUrlValid}
                        className="h-9 rounded-control border border-bartlett bg-bartlett px-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save note'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold">{event.title}</div>
                        <div className="mt-0.5 font-mono text-[11px] uppercase tracking-wide text-muted">
                          {event.type} · {formatEventTime(event.occurredAt)}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {openUrl && (
                          <a
                            href={openUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex h-8 items-center gap-1 rounded-full border border-border px-2 font-mono text-[11px] text-muted hover:border-bartlett/40 hover:text-ink"
                          >
                            <LinkIcon size={12} />
                            Open video
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => startEdit(event)}
                          className="inline-flex h-8 items-center gap-1 rounded-full border border-border px-2 font-mono text-[11px] text-muted hover:border-bartlett/40 hover:text-ink"
                        >
                          <Pencil size={12} />
                          {prospectTouch && !event.body ? 'Add context' : 'Edit'}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteEvent(event)}
                          disabled={deletingId === event.id || Boolean(prospectTouch) || event.id.startsWith('prospect_timeline_')}
                          title={prospectTouch || event.id.startsWith('prospect_timeline_') ? 'This contact is synchronized with the prospect tracker and cannot be deleted here.' : undefined}
                          className="inline-flex h-8 items-center gap-1 rounded-full border border-flag-critical/30 px-2 font-mono text-[11px] text-flag-critical hover:bg-flag-critical/5 disabled:opacity-50"
                        >
                          <Trash2 size={12} />
                          Delete
                        </button>
                      </div>
                    </div>
                    {metadata?.sourceLabel && (
                      <div className="mt-1 font-mono text-[10px] font-semibold uppercase tracking-wide text-bartlett">
                        Source · {metadata.sourceLabel}
                      </div>
                    )}
                    {prospectTouch && (
                      <div className="mt-1 font-mono text-[10px] font-semibold uppercase tracking-wide text-bartlett">
                        Auto-logged from Prospects · +{prospectTouch.points} point
                        {prospectTouch.points === 1 ? '' : 's'}
                      </div>
                    )}
                    {event.body && (
                      <p className="mt-2 whitespace-pre-wrap text-sm text-muted">{event.body}</p>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatEventTime(value: number): string {
  return new Date(value * 1000).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatEventDate(value: number): string {
  return new Date(value * 1000).toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function eventDateIso(value: number): string {
  return new Date(value * 1000).toISOString().slice(0, 10);
}

function todayInputValue(): string {
  return dateToInputValue(new Date());
}

function dateToInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dateInputToUnix(value: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return Math.floor(Date.now() / 1000);
  const [yearRaw, monthRaw, dayRaw] = value.split('-');
  if (!yearRaw || !monthRaw || !dayRaw) return Math.floor(Date.now() / 1000);
  const year = Number.parseInt(yearRaw, 10);
  const month = Number.parseInt(monthRaw, 10);
  const day = Number.parseInt(dayRaw, 10);
  const date = new Date(year, month - 1, day, 12, 0, 0);
  if (Number.isNaN(date.getTime())) return Math.floor(Date.now() / 1000);
  return Math.floor(date.getTime() / 1000);
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-control border border-dashed border-border bg-white px-4 py-5 text-sm text-muted">
      {text}
    </div>
  );
}

function SectionHeader({ children }: { children: ReactNode }) {
  return (
    <h2 className="border-b border-border pb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">
      {children}
    </h2>
  );
}

// Read-only: refreshed every Monday from CapWages. Scott's own notes stay in
// "Recent details" and are never changed by the sync.
function NhlContractSummary({ contract }: { contract: NhlContract }) {
  const checked = new Date(contract.checkedAt * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return (
    <div className="text-sm leading-6">
      <p className="font-semibold text-ink">
        {[contract.totalValue, contract.length].filter(Boolean).join(' × ')}
        {contract.startSeason && contract.endSeason && <span className="font-normal text-muted"> · {contract.startSeason} to {contract.endSeason}</span>}
      </p>
      <p className="text-muted">
        {[
          contract.currentCapHit && `Cap hit this season ${contract.currentCapHit}`,
          contract.expiryStatus && `Ends ${contract.expiryStatus}`,
          contract.signingDate && `Signed ${contract.signingDate}${contract.signingTeam ? ` (${contract.signingTeam})` : ''}`,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      <a href={contract.sourceUrl} target="_blank" rel="noreferrer" className="focus-ring font-mono text-[11px] text-muted underline hover:text-ink">
        CapWages · checked {checked}
      </a>
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-semibold uppercase tracking-wide text-muted">
        {label}
      </label>
      <div>{children}</div>
    </div>
  );
}

function BackBar() {
  const location = useLocation();
  const from = typeof location.state?.from === 'string' ? location.state.from : '/people';
  return (
    <Link
      to={from}
      className="inline-flex items-center gap-1 text-sm font-medium text-muted hover:text-ink"
    >
      <ChevronLeft size={16} aria-hidden="true" />
      <span>Back to People</span>
    </Link>
  );
}
